import { action, flow, makeObservable, observable } from 'mobx';
import { CheckoutProfile, checkoutProfileCast } from '../types/paymentCheckout';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus, TBooleanInNumber } from '~/types/main';
import { arrayMerge } from '~/utils/formatter';
import { booleanToNumber } from '~/utils/helpers';

const defaultSelectedCheckoutProfile = {
  id: 0,
  name: '',
  is_default: 0 as TBooleanInNumber,
  autoOnlinePayment: false,
  subChannel: '',
  enabled: false,
  isProductionKey: false,
  secretProdKey: '',
  publicProdKey: '',
  secretSandboxKey: '',
  publicSandboxKey: '',
  branches: [],
};

class PaymentCheckoutStore {
  constructor() {
    makeObservable(this);
  }

  @observable checkoutProfiles: CheckoutProfile[] = [];
  @observable selectedCheckoutProfile: CheckoutProfile = defaultSelectedCheckoutProfile;
  @observable isDataEdited = false;
  @observable loadingStatus = LoadingStatus.Init;

  @action public init = () => {
    this.loadingStatus = LoadingStatus.Init;
    this.isDataEdited = false;

    this.fetchProfiles();
  };

  @action changeSelectedProfileData = (data: Partial<CheckoutProfile>) => {
    this.selectedCheckoutProfile = { ...this.selectedCheckoutProfile, ...data };
    this.isDataEdited = true;
  };

  @action private fetchProfiles = flow(function* (this: PaymentCheckoutStore) {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const profiles = yield client.get('v3/integrations/checkout');

      this.checkoutProfiles = Object.values(profiles).map(checkoutProfileCast);
      // NOTE: there is always default profile in the list
      this.selectedCheckoutProfile = this.checkoutProfiles[0];

      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'PaymentCheckoutStore fetchProfiles' });
    }
  });

  @action updateProfile = flow(function* (this: PaymentCheckoutStore) {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const res = yield client.post('v3/integrations/checkout', this._getApiParams());

      if (res.data) {
        const updatedProfile = checkoutProfileCast(res.data);
        this.checkoutProfiles = arrayMerge(this.checkoutProfiles, [updatedProfile], 'id');
      }

      this.loadingStatus = LoadingStatus.Success;
      this.isDataEdited = false;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'PaymentCheckoutStore updateProfile' });
    }
  });

  @action selectProfile = (id: number) => {
    const profile = this.checkoutProfiles.find((profile) => profile.id === id);

    if (!profile) return;

    this.selectedCheckoutProfile = profile;
    this.isDataEdited = false;
  };

  @action reset = () => {
    this.checkoutProfiles = [];
    this.selectedCheckoutProfile = defaultSelectedCheckoutProfile;
    this.isDataEdited = false;
    this.loadingStatus = LoadingStatus.Init;
  };

  @action private _getApiParams = () => ({
    ...this.selectedCheckoutProfile,
    enabled: booleanToNumber(this.selectedCheckoutProfile.enabled),
    isProductionKey: booleanToNumber(this.selectedCheckoutProfile.isProductionKey),
    auto_accept_online_payment: booleanToNumber(this.selectedCheckoutProfile.autoOnlinePayment),
    franchise_id: this.selectedCheckoutProfile.id,
    sub_channel: this.selectedCheckoutProfile.subChannel,
  });
}

const paymentCheckoutStore = new PaymentCheckoutStore();

export default paymentCheckoutStore;
